const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─── Helpers ─────────────────────────────────────────────────────────────────
// Nettoyage des valeurs corrompues ("undefined"/"null" en string)
function getToken() {
  const t = localStorage.getItem("78pressing_token");
  if (!t || t === "undefined" || t === "null") { localStorage.removeItem("78pressing_token"); return null; }
  return t;
}
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
async function handleResponse(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Erreur ${res.status}`);
    // Corps et statut conservés : certains appelants en ont besoin
    // (ex. otp_required sur l'espace client).
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ─── Simple in-memory cache (TTL-based) ────────────────────────────────────
const _cache = {};
async function cachedGet(url, headers, ttlMs = 30000) {
  const key = url;
  const now = Date.now();
  if (_cache[key] && (now - _cache[key].ts) < ttlMs) return _cache[key].data;
  const res = await fetch(url, { headers });
  const data = await handleResponse(res);
  _cache[key] = { data, ts: now };
  return data;
}
export function clearCache(pattern) {
  if (!pattern) { Object.keys(_cache).forEach(k => delete _cache[k]); return; }
  Object.keys(_cache).filter(k => k.includes(pattern)).forEach(k => delete _cache[k]);
}
// Auto-clear cache after mutations
async function mutate(url, opts) {
  const res = await fetch(url, opts);
  const data = await handleResponse(res);
  // Clear cache for the base resource path (e.g. /api/admin/services/123 -> /api/admin/services)
  const basePath = url.replace(BASE, "").replace(/\/\d+$/, "");
  clearCache(basePath);
  clearCache("stats"); // stats may change after any mutation
  return data;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export async function login(loginVal, pass) {
  const res = await fetch(`${BASE}/api/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ login: loginVal, pass }),
  });
  const data = await handleResponse(res);
  // Ne stocker que si présent (MFA requis → pas de token/user encore)
  if (data.token) localStorage.setItem("78pressing_token", data.token);
  if (data.user)  localStorage.setItem("78pressing_user", JSON.stringify(data.user));
  return data;
}
export function logout() {
  localStorage.removeItem("78pressing_token");
  localStorage.removeItem("78pressing_user");
}
export function getStoredUser() {
  try {
    const u = localStorage.getItem("78pressing_user");
    if (!u || u === "undefined" || u === "null") { localStorage.removeItem("78pressing_user"); return null; }
    const parsed = JSON.parse(u);
    if (!parsed || !parsed.role) { localStorage.removeItem("78pressing_user"); return null; }
    return parsed;
  } catch { localStorage.removeItem("78pressing_user"); return null; }
}

// ─── Commandes ────────────────────────────────────────────────────────────────
export async function getOrders() {
  const res = await fetch(`${BASE}/api/orders`, { headers: { "Content-Type": "application/json", ...authHeaders() } });
  return handleResponse(res);
}
export async function getOrder(id) {
  const res = await fetch(`${BASE}/api/orders/${id}`, { headers: { "Content-Type": "application/json" } });
  return handleResponse(res);
}
export async function createOrder(order) {
  const res = await fetch(`${BASE}/api/orders`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(order),
  });
  return handleResponse(res);
}
export async function updateOrder(id, updates) {
  const res = await fetch(`${BASE}/api/orders/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(updates),
  });
  return handleResponse(res);
}
export async function deleteOrder(id) {
  const res = await fetch(`${BASE}/api/orders/${id}`, {
    method: "DELETE", headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── WhatsApp ─────────────────────────────────────────────────────────────────
export async function sendRecuWhatsApp({ phone, pdfBase64, filename, caption, order_id }) {
  const res = await fetch(`${BASE}/api/whatsapp/send-recu`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ phone, pdfBase64, filename, caption, order_id }),
  });
  return handleResponse(res);
}

// ─── Services (public) ────────────────────────────────────────────────────────
export async function getServices() {
  const res = await fetch(`${BASE}/api/services`);
  return handleResponse(res);
}
export async function getPricing() {
  const res = await fetch(`${BASE}/api/pricing`);
  return handleResponse(res);
}
export async function getBranding() {
  const res = await fetch(`${BASE}/api/branding`);
  return handleResponse(res);
}

// ─── Admin — Services ─────────────────────────────────────────────────────────
export async function adminGetServices() {
  return cachedGet(`${BASE}/api/admin/services`, { ...authHeaders() }, 60000);
}
export async function adminCreateService(data) {
  return mutate(`${BASE}/api/admin/services`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdateService(id, data) {
  return mutate(`${BASE}/api/admin/services/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeleteService(id) {
  return mutate(`${BASE}/api/admin/services/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}

// ─── Admin — Pricing ──────────────────────────────────────────────────────────
export async function adminGetPricing() {
  return cachedGet(`${BASE}/api/admin/pricing`, { ...authHeaders() }, 60000);
}
export async function adminCreatePricing(data) {
  return mutate(`${BASE}/api/admin/pricing`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdatePricing(id, data) {
  return mutate(`${BASE}/api/admin/pricing/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeletePricing(id) {
  return mutate(`${BASE}/api/admin/pricing/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}

// ─── MFA ──────────────────────────────────────────────────────────────────────
export async function verifyMFA(temp_token, totp_code) {
  const res = await fetch(`${BASE}/api/mfa/verify`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ temp_token, totp_code }),
  });
  const data = await handleResponse(res);
  localStorage.setItem("78pressing_token", data.token);
  localStorage.setItem("78pressing_user", JSON.stringify(data.user));
  return data;
}
export async function adminGetMFAStatus() {
  const res = await fetch(`${BASE}/api/admin/mfa/status`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminSetupMFA() {
  const res = await fetch(`${BASE}/api/admin/mfa/setup`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  return handleResponse(res);
}
export async function adminConfirmMFA(secret, totp_code) {
  const res = await fetch(`${BASE}/api/admin/mfa/confirm`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ secret, totp_code }),
  });
  return handleResponse(res);
}
export async function adminDisableMFA(totp_code) {
  const res = await fetch(`${BASE}/api/admin/mfa/disable`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ totp_code }),
  });
  return handleResponse(res);
}

// ─── Admin — Utilisateurs ─────────────────────────────────────────────────────
export async function adminGetUsers() {
  const res = await fetch(`${BASE}/api/admin/users`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminCreateUser(data) {
  const res = await fetch(`${BASE}/api/admin/users`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminUpdateUser(id, data) {
  const res = await fetch(`${BASE}/api/admin/users/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminDeleteUser(id) {
  const res = await fetch(`${BASE}/api/admin/users/${id}`, {
    method: "DELETE", headers: { ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Stats (Manager/Admin) ──────────────────────────────────────────────────
export async function getStats() {
  return cachedGet(`${BASE}/api/stats`, { ...authHeaders() }, 30000);
}

// ─── Client Search ──────────────────────────────────────────────────────────
export async function searchClient(phone) {
  const res = await fetch(`${BASE}/api/clients/search?phone=${encodeURIComponent(phone)}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
// ─── Admin Clients ──────────────────────────────────────────────────────────
export async function adminGetClients(search) {
  const q = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await fetch(`${BASE}/api/admin/clients${q}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminGetClientOrders(phone) {
  const res = await fetch(`${BASE}/api/admin/clients/${encodeURIComponent(phone)}/orders`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Order Items ─────────────────────────────────────────────────────────────
export async function saveOrderItems(orderId, items) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/items`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ items }),
  });
  return handleResponse(res);
}
export async function getOrderItems(orderId) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/items`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Order Photos ────────────────────────────────────────────────────────────
export async function uploadOrderPhoto(orderId, photoData) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/photos`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ photo_data: photoData }),
  });
  return handleResponse(res);
}
export async function getOrderPhotos(orderId) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/photos`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Payment ─────────────────────────────────────────────────────────────────
export async function updatePayment(orderId, data) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/payment`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ─── Loyalty ─────────────────────────────────────────────────────────────────
export async function getLoyalty(phone) {
  const res = await fetch(`${BASE}/api/loyalty/${encodeURIComponent(phone)}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
// Delivery fees config
export async function getDeliveryFeesConfig() {
  const res = await fetch(`${BASE}/api/delivery-fees-config`);
  return handleResponse(res);
}
export async function saveDeliveryFeesConfig(config) {
  return mutate(`${BASE}/api/admin/delivery-fees-config`, {
    method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(config),
  });
}

export async function getLoyaltyConfig() {
  const res = await fetch(`${BASE}/api/loyalty-config`);
  return handleResponse(res);
}
export async function saveLoyaltyConfig(config) {
  return mutate(`${BASE}/api/admin/loyalty-config`, {
    method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(config),
  });
}
export async function useLoyaltyPoints(phone, points_used, order_id) {
  const res = await fetch(`${BASE}/api/loyalty/use`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ phone, points_used, order_id }),
  });
  return handleResponse(res);
}

// ─── Assignments (Collector / Delivery) ─────────────────────────────────────
export async function assignCollector(orderId, collector_id) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/assign-collector`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ collector_id }),
  });
  return handleResponse(res);
}
export async function assignDelivery(orderId, delivery_id) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/assign-delivery`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ delivery_id }),
  });
  return handleResponse(res);
}
export async function getAssignableUsers() {
  const res = await fetch(`${BASE}/api/admin/assignable-users`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function getMyAssignments() {
  const res = await fetch(`${BASE}/api/my-assignments`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Promo Codes ────────────────────────────────────────────────────────────
export async function adminGetPromoCodes() {
  const res = await fetch(`${BASE}/api/admin/promo-codes`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminCreatePromoCode(data) {
  return mutate(`${BASE}/api/admin/promo-codes`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdatePromoCode(id, data) {
  return mutate(`${BASE}/api/admin/promo-codes/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeletePromoCode(id) {
  return mutate(`${BASE}/api/admin/promo-codes/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}
export async function validatePromo(code, amount) {
  const res = await fetch(`${BASE}/api/promo/validate`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, amount }),
  });
  return handleResponse(res);
}
export async function getPromoConfig() {
  const res = await fetch(`${BASE}/api/admin/promo-config`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function savePromoConfig(config) {
  return mutate(`${BASE}/api/admin/promo-config`, { method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(config) });
}
export async function getReferralCode(phone) {
  const res = await fetch(`${BASE}/api/referral/code/${encodeURIComponent(phone)}`, { headers: { ...clientAuthHeaders() } });
  return handleResponse(res);
}
export async function getReferralStats() {
  const res = await fetch(`${BASE}/api/admin/referral-stats`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Visitor Analytics ──────────────────────────────────────────────────────
export async function trackPageView(path = "/") {
  try {
    let vid = localStorage.getItem("setal_vid");
    if (!vid) {
      vid = (crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2));
      localStorage.setItem("setal_vid", vid);
    }
    // Session-based dedup (avoid logging every re-render)
    const lastKey = `setal_tracked_${path}`;
    const last = sessionStorage.getItem(lastKey);
    if (last && Date.now() - parseInt(last) < 30 * 60 * 1000) return; // 30min
    sessionStorage.setItem(lastKey, String(Date.now()));
    await fetch(`${BASE}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitor_id: vid, path, referrer: document.referrer || "" }),
      keepalive: true,
    });
  } catch {}
}
export async function getVisitorsStats() {
  const res = await fetch(`${BASE}/api/admin/visitors-stats`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── WhatsApp Inbox ─────────────────────────────────────────────────────────
export async function getWaInbox() {
  const res = await fetch(`${BASE}/api/admin/wa-inbox`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function getWaConversation(phone) {
  const res = await fetch(`${BASE}/api/admin/wa-inbox/${encodeURIComponent(phone)}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function sendWaReply(phone, message) {
  const res = await fetch(`${BASE}/api/admin/wa-inbox/${encodeURIComponent(phone)}/reply`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ message }),
  });
  return handleResponse(res);
}
export async function getWaUnreadCount() {
  const res = await fetch(`${BASE}/api/admin/wa-inbox/unread/count`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Audit Logs ──────────────────────────────────────────────────────────────
export async function getAuditLogs(page = 1, limit = 50) {
  const res = await fetch(`${BASE}/api/admin/audit-logs?page=${page}&limit=${limit}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Backup ──────────────────────────────────────────────────────────────────
export async function createBackup() {
  const res = await fetch(`${BASE}/api/backup`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Notifications History ───────────────────────────────────────────────────
export async function getNotifications() {
  const res = await fetch(`${BASE}/api/admin/notifications`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Collection Slots ───────────────────────────────────────────────────────
export async function getCollectionSlots() {
  const res = await fetch(`${BASE}/api/collection-slots`);
  return handleResponse(res);
}
export async function getAvailableSlots(date) {
  const res = await fetch(`${BASE}/api/collection-slots/available?date=${date}`);
  return handleResponse(res);
}
export async function adminGetCollectionSlots() {
  const res = await fetch(`${BASE}/api/admin/collection-slots`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminCreateCollectionSlot(data) {
  const res = await fetch(`${BASE}/api/admin/collection-slots`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminUpdateCollectionSlot(id, data) {
  const res = await fetch(`${BASE}/api/admin/collection-slots/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminDeleteCollectionSlot(id) {
  const res = await fetch(`${BASE}/api/admin/collection-slots/${id}`, {
    method: "DELETE", headers: { ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Delivery Slots ────────────────────────────────────────────────────────
export async function getDeliverySlots() {
  const res = await fetch(`${BASE}/api/delivery-slots`);
  return handleResponse(res);
}
export async function getAvailableDeliverySlots(date) {
  const res = await fetch(`${BASE}/api/delivery-slots/available?date=${date}`);
  return handleResponse(res);
}
// Client confirms delivery reception
export async function confirmDelivery(orderId) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/confirm-delivery`, {
    method: "POST", headers: { "Content-Type": "application/json" },
  });
  return handleResponse(res);
}
// Get saved client info
export async function getClientInfo(phone) {
  const res = await fetch(`${BASE}/api/clients/info/${encodeURIComponent(phone)}`);
  return handleResponse(res);
}
// List all clients (manager/admin)
export async function getClientsList() {
  const res = await fetch(`${BASE}/api/clients/list`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
// ─── Espace client : vérification par SMS ───────────────────────────────────
// L'historique d'un client n'est plus librement consultable : il faut un jeton
// « client » obtenu par code SMS (ou un jeton du personnel). Le serveur renvoie
// { otp_required: false } tant que l'envoi de SMS n'est pas configuré.
const CLIENT_TOKEN_KEY = "78pressing_client_token";

function clientAuthHeaders() {
  // Jeton du personnel prioritaire (managers/collecteurs consultant une fiche)
  const staff = authHeaders();
  if (staff.Authorization) return staff;
  try {
    const t = localStorage.getItem(CLIENT_TOKEN_KEY);
    if (t && t !== "undefined") return { Authorization: `Bearer ${t}` };
  } catch {}
  return {};
}

export async function requestClientCode(phone) {
  const res = await fetch(`${BASE}/api/clients/verify/request`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  return handleResponse(res);
}

export async function confirmClientCode(phone, code) {
  const res = await fetch(`${BASE}/api/clients/verify/confirm`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone, code }),
  });
  const data = await handleResponse(res);
  if (data.token) { try { localStorage.setItem(CLIENT_TOKEN_KEY, data.token); } catch {} }
  return data;
}

export function clearClientToken() {
  try { localStorage.removeItem(CLIENT_TOKEN_KEY); } catch {}
}

// Get client order history by phone (jeton client ou personnel requis dès que le SMS est configuré)
export async function getClientOrders(phone) {
  const res = await fetch(`${BASE}/api/clients/${encodeURIComponent(phone.replace(/\s/g, ""))}/orders`, {
    headers: { ...clientAuthHeaders() },
  });
  return handleResponse(res);
}
export async function chooseDeliverySlot(orderId, delivery_date, delivery_time) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/delivery`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ delivery_date, delivery_time }),
  });
  return handleResponse(res);
}
export async function adminGetDeliverySlots() {
  const res = await fetch(`${BASE}/api/admin/delivery-slots`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminCreateDeliverySlot(data) {
  const res = await fetch(`${BASE}/api/admin/delivery-slots`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminUpdateDeliverySlot(id, data) {
  const res = await fetch(`${BASE}/api/admin/delivery-slots/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function adminDeleteDeliverySlot(id) {
  const res = await fetch(`${BASE}/api/admin/delivery-slots/${id}`, {
    method: "DELETE", headers: { ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Articles ───────────────────────────────────────────────────────────────
export async function getArticles() {
  const res = await fetch(`${BASE}/api/articles`);
  return handleResponse(res);
}
export async function getLandingArticles() {
  const res = await fetch(`${BASE}/api/articles?landing=true`);
  return handleResponse(res);
}
export async function adminGetArticles() {
  return cachedGet(`${BASE}/api/admin/articles`, { ...authHeaders() }, 60000);
}
export async function adminCreateArticle(data) {
  return mutate(`${BASE}/api/admin/articles`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdateArticle(id, data) {
  return mutate(`${BASE}/api/admin/articles/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeleteArticle(id) {
  return mutate(`${BASE}/api/admin/articles/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}

// ─── Etablissements ─────────────────────────────────────────────────────────
export async function adminGetEtablissements() {
  return cachedGet(`${BASE}/api/admin/etablissements`, { ...authHeaders() }, 60000);
}
export async function adminCreateEtablissement(data) {
  return mutate(`${BASE}/api/admin/etablissements`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdateEtablissement(id, data) {
  return mutate(`${BASE}/api/admin/etablissements/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeleteEtablissement(id) {
  return mutate(`${BASE}/api/admin/etablissements/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}

// ─── Expense Categories ─────────────────────────────────────────────────────
export async function adminGetExpenseCategories(etabId) {
  const q = etabId ? `?etab_id=${etabId}` : "";
  const res = await fetch(`${BASE}/api/admin/expense-categories${q}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function adminCreateExpenseCategory(data) {
  return mutate(`${BASE}/api/admin/expense-categories`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminUpdateExpenseCategory(id, data) {
  return mutate(`${BASE}/api/admin/expense-categories/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data) });
}
export async function adminDeleteExpenseCategory(id) {
  return mutate(`${BASE}/api/admin/expense-categories/${id}`, { method: "DELETE", headers: { ...authHeaders() } });
}

// ─── Expenses ───────────────────────────────────────────────────────────────
export async function getExpenses(filters = {}) {
  const params = new URLSearchParams();
  if (filters.etab_id) params.set("etab_id", filters.etab_id);
  if (filters.month) params.set("month", filters.month);
  if (filters.year) params.set("year", filters.year);
  if (filters.category_id) params.set("category_id", filters.category_id);
  const res = await fetch(`${BASE}/api/expenses?${params}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function createExpense(data) {
  const res = await fetch(`${BASE}/api/expenses`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function updateExpense(id, data) {
  const res = await fetch(`${BASE}/api/expenses/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function deleteExpense(id) {
  const res = await fetch(`${BASE}/api/expenses/${id}`, {
    method: "DELETE", headers: { ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Incomes ────────────────────────────────────────────────────────────────
export async function getIncomes(filters = {}) {
  const params = new URLSearchParams();
  if (filters.etab_id) params.set("etab_id", filters.etab_id);
  if (filters.month) params.set("month", filters.month);
  if (filters.year) params.set("year", filters.year);
  const res = await fetch(`${BASE}/api/incomes?${params}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function createIncome(data) {
  const res = await fetch(`${BASE}/api/incomes`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function updateIncome(id, data) {
  const res = await fetch(`${BASE}/api/incomes/${id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function deleteIncome(id) {
  const res = await fetch(`${BASE}/api/incomes/${id}`, {
    method: "DELETE", headers: { ...authHeaders() },
  });
  return handleResponse(res);
}

// ─── Finance ────────────────────────────────────────────────────────────────
export async function getFinanceSummary(filters = {}) {
  const params = new URLSearchParams();
  if (filters.etab_id) params.set("etab_id", filters.etab_id);
  if (filters.month) params.set("month", filters.month);
  if (filters.year) params.set("year", filters.year);
  const res = await fetch(`${BASE}/api/finance/summary?${params}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
// ─── Collector Tracking ────────────────────────────────────────────────────
export async function updateCollectorLocation(data) {
  const res = await fetch(`${BASE}/api/collector/location`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function stopCollectorTracking() {
  const res = await fetch(`${BASE}/api/collector/location/stop`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  return handleResponse(res);
}
export async function getCollectorLocation(orderId) {
  const res = await fetch(`${BASE}/api/orders/${orderId}/collector-location`);
  return handleResponse(res);
}

// ─── Push Notifications ─────────────────────────────────────────────────────
export async function getVapidKey() {
  const res = await fetch(`${BASE}/api/push/vapid-key`);
  return handleResponse(res);
}
export async function subscribePush(phone, subscription) {
  const res = await fetch(`${BASE}/api/push/subscribe`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone, subscription }),
  });
  return handleResponse(res);
}

// ─── User-Establishment Assignments ────────────────────────────────────────
export async function setUserEstablishments(userId, etab_ids) {
  const res = await fetch(`${BASE}/api/admin/users/${userId}/establishments`, {
    method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ etab_ids }),
  });
  return handleResponse(res);
}

// ─── Admin Settings ────────────────────────────────────────────────────────
export async function getSettings() {
  const res = await fetch(`${BASE}/api/admin/settings`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function saveSettings(settings) {
  const res = await fetch(`${BASE}/api/admin/settings`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ settings }),
  });
  return handleResponse(res);
}
// Envoie un SMS réel via MTarget pour valider les identifiants enregistrés.
export async function testSMS(phone, message) {
  const res = await fetch(`${BASE}/api/admin/sms/test`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ phone, message }),
  });
  return handleResponse(res);
}
export async function testWhatsApp(phone_id, token, test_phone) {
  const res = await fetch(`${BASE}/api/admin/whatsapp/test`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ phone_id, token, test_phone }),
  });
  return handleResponse(res);
}

export async function broadcastWhatsApp(message, phones) {
  const res = await fetch(`${BASE}/api/admin/whatsapp/broadcast`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ message, phones }),
  });
  return handleResponse(res);
}

// ─── Weekly Report ─────────────────────────────────────────────────────────
export async function getWeeklyReport() {
  const res = await fetch(`${BASE}/api/admin/report/weekly`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Finance Export ─────────────────────────────────────────────────────────
export async function getFinanceExport(month, year, etab_id) {
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  if (year) params.set("year", year);
  if (etab_id) params.set("etab_id", etab_id);
  const res = await fetch(`${BASE}/api/finance/export?${params}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Finance ────────────────────────────────────────────────────────────────
export async function getFinanceGlobal(filters = {}) {
  const params = new URLSearchParams();
  if (filters.month) params.set("month", filters.month);
  if (filters.year) params.set("year", filters.year);
  const res = await fetch(`${BASE}/api/finance/global?${params}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}

// ─── Reviews ───────────────────────────────────────────────────────────────
export async function submitReview(data) {
  const res = await fetch(`${BASE}/api/reviews`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
  });
  return handleResponse(res);
}
export async function getReviews(limit) {
  const res = await fetch(`${BASE}/api/reviews?limit=${limit || 50}`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
export async function getReviewStats() {
  const res = await fetch(`${BASE}/api/reviews/stats`, { headers: { ...authHeaders() } });
  return handleResponse(res);
}
